import { useEffect, useMemo, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/Edit';
import PrintIcon from '@mui/icons-material/Print';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { formatTimestamp } from '../lib/format';
import { DETAIL, euro, germanDate, num } from '../lib/euerFormat';
import { periodRange, PERIOD_OPTIONS, type PeriodKey } from '../lib/period';
import { PageHelp } from '../components/PageHelp';
import {
  EuerTable,
  Line,
  type AssetLine,
  type EuerData,
  type VoucherRow,
} from '../components/EuerTable';
import type { Division, TaxAreaEntry } from '../lib/types';

interface EuerResponse {
  euer: EuerData;
  divisions: Division[];
  taxAreas: TaxAreaEntry[];
  dataAsOf: string | null;
}

/** Aufklappbare Liste der Belege hinter einer Zeile der Überleitung. */
function Vouchers({
  title,
  rows,
  divisionName,
}: {
  title: string;
  rows: VoucherRow[];
  divisionName: (id?: string) => string;
}) {
  const sum = rows.reduce((s, r) => s + r.netCents, 0);
  return (
    <Accordion disableGutters variant="outlined" disabled={rows.length === 0}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="body2" sx={{ flex: 1 }}>
          {title} – {rows.length} {rows.length === 1 ? 'Beleg' : 'Belege'}
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'nowrap', mr: 1 }}>
          {euro(sum)}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Datum</TableCell>
              <TableCell>Beleg</TableCell>
              <TableCell>Partner / Text</TableCell>
              <TableCell>Konto</TableCell>
              <TableCell>Abteilung</TableCell>
              <TableCell {...num}>Netto</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.postingId}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{germanDate(r.stichtag)}</TableCell>
                <TableCell>{r.voucherNumber}</TableCell>
                <TableCell>
                  {r.partner}
                  {r.text && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {r.text}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {r.accountNumber} {r.accountName}
                </TableCell>
                <TableCell>{divisionName(r.divisionId)}</TableCell>
                <TableCell {...num}>{euro(r.netCents)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </AccordionDetails>
    </Accordion>
  );
}

/**
 * D-23: EÜR wie in SevDesk -- Gruppen als Zwischensummen, Abschreibungen statt Anschaffung von
 * Anlagen über der GWG-Grenze, Umsatzsteuer-Block. Alle Rollen, je für ihre Abteilungen.
 */
export default function EuerPage() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  const today = new Date().toISOString().slice(0, 10);
  const thisYear = Number(today.slice(0, 4));
  const [year, setYear] = useState(thisYear);
  const [period, setPeriod] = useState<PeriodKey>('jahr');
  const [division, setDivision] = useState('');
  const [taxArea, setTaxArea] = useState('');
  const [data, setData] = useState<EuerResponse>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<AssetLine>();
  const [months, setMonths] = useState('');
  const [dialogError, setDialogError] = useState<string>();
  const [printMenu, setPrintMenu] = useState<HTMLElement>();
  const [summaryPrint, setSummaryPrint] = useState(false);

  const { from, to } = periodRange(year, period, today);
  const query = useMemo(() => {
    const q: Record<string, string> = { from, to };
    if (division) q.division = division;
    if (taxArea) q.taxArea = taxArea;
    return q;
  }, [from, to, division, taxArea]);

  async function load() {
    setLoading(true);
    setError(undefined);
    try {
      setData(await api.get<EuerResponse>(`/admin/report/euer?${new URLSearchParams(query)}`));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'EÜR konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // load hängt nur von query ab
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  async function saveUsefulLife() {
    if (!editing) return;
    const n = Number(months.trim());
    if (!Number.isInteger(n) || n < 1) {
      setDialogError('Bitte die Nutzungsdauer in ganzen Monaten angeben, z.B. 60 für 5 Jahre.');
      return;
    }
    try {
      await api.put('/admin/report/euer/assets', {
        postingId: editing.postingId,
        usefulLifeMonths: n,
      });
      setEditing(undefined);
      await load();
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
    }
  }

  const e = data?.euer;
  const divisionName = (id?: string) =>
    data?.divisions.find((d) => d.divisionId === id)?.name ?? '';
  const scope = [
    division ? divisionName(division) : 'Alle Abteilungen',
    taxArea ? (data?.taxAreas.find((t) => t.key === taxArea)?.name ?? taxArea) : 'Alle Bereiche',
  ].join(' · ');

  /** Druckt die Seite; der Titel wird beim Speichern als PDF zum Dateinamen. */
  function print(summary: boolean) {
    setPrintMenu(undefined);
    setSummaryPrint(summary);
    // erst drucken, wenn das Menü zu ist und die Variante gerendert wurde
    window.setTimeout(() => {
      const title = document.title;
      document.title = `EÜR ${germanDate(from)}-${germanDate(to)}${division ? ` ${divisionName(division)}` : ''}${summary ? ' Summen' : ''}`;
      window.print();
      document.title = title;
    }, 300);
  }

  return (
    <Stack
      spacing={3}
      data-print={summaryPrint ? 'summen' : undefined}
      sx={{
        '@media print': {
          // Überleitung auf neuer Seite; nur Summen: ohne Konten, Belege, Anlagen.
          '& .euer-break': { breakBefore: 'page' },
          [`&[data-print="summen"] .${DETAIL}`]: { display: 'none' },
          // Beleglisten der Überleitung aufgeklappt drucken, Schatten und Bedienelemente weglassen.
          '& .MuiCollapse-root': { height: 'auto !important', visibility: 'visible !important' },
          '& .MuiAccordionSummary-expandIconWrapper': { display: 'none' },
          '& .MuiPaper-root': { boxShadow: 'none' },
          '& tr': { breakInside: 'avoid' },
        },
      }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="h4">EÜR</Typography>
        <Box sx={{ flex: 1, displayPrint: 'none' }}>
          <PageHelp text="Einnahmenüberschussrechnung wie in SevDesk, gegliedert nach den Gruppen. Unterschied zum Dashboard: Anlagen über der GWG-Grenze zählen nicht mit ihrer Anschaffung, sondern mit ihrer monatlich gerechneten Abschreibung im Zeitraum (für die Budgetsteuerung zählt im Dashboard die Zahlung); GWG stehen wie in SevDesk unter den Abschreibungen (Zeile GWG), am Ergebnis ändert das nichts. Dazu kommt die Umsatzsteuer: vereinnahmte Umsatzsteuer und Erstattungen des Finanzamts als Einnahme, gezahlte Vorsteuer und Zahlungen an das Finanzamt als Ausgabe. Mit einer Abteilung gewählt ist es deren Anteil, einschließlich der Umbuchungen zwischen den Abteilungen." />
        </Box>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={(ev) => setPrintMenu(ev.currentTarget)}
          disabled={!e}
          sx={{ displayPrint: 'none' }}
        >
          Drucken
        </Button>
        <Menu
          anchorEl={printMenu}
          open={Boolean(printMenu)}
          onClose={() => setPrintMenu(undefined)}
        >
          <MenuItem onClick={() => print(false)}>Vollständig</MenuItem>
          <MenuItem onClick={() => print(true)}>Nur Summen</MenuItem>
        </Menu>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ displayPrint: 'none' }}>
        <TextField
          select
          size="small"
          label="Jahr"
          value={year}
          onChange={(ev) => setYear(Number(ev.target.value))}
          disabled={period === 'alle'}
          sx={{ minWidth: 110 }}
        >
          {Array.from({ length: thisYear - 2017 }, (_, i) => thisYear - i).map((y) => (
            <MenuItem key={y} value={y}>
              {y}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Zeitraum"
          value={period}
          onChange={(ev) => setPeriod(ev.target.value as PeriodKey)}
          sx={{ minWidth: 160 }}
        >
          {PERIOD_OPTIONS.map((p) => (
            <MenuItem key={p.key} value={p.key}>
              {p.label}
            </MenuItem>
          ))}
        </TextField>
        {(data?.divisions.length ?? 0) > 1 && (
          <TextField
            select
            size="small"
            label="Abteilung"
            value={division}
            onChange={(ev) => setDivision(ev.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">Alle Abteilungen</MenuItem>
            {data!.divisions.map((d) => (
              <MenuItem key={d.divisionId} value={d.divisionId}>
                {d.name}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          select
          size="small"
          label="Steuerlicher Bereich"
          value={taxArea}
          onChange={(ev) => setTaxArea(ev.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">Alle Bereiche</MenuItem>
          {(data?.taxAreas ?? []).map((t) => (
            <MenuItem key={t.key} value={t.key}>
              {t.name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {data && (
        <Typography variant="body2" color="text.secondary">
          <Box component="span" sx={{ display: 'none', displayPrint: 'inline' }}>
            Sp.Vgg. Lagerlechfeld-Graben · {scope} ·{' '}
          </Box>
          {germanDate(from)} bis {germanDate(to)} · Datenstand:{' '}
          {data.dataAsOf ? formatTimestamp(data.dataAsOf) : 'noch kein Import'}
        </Typography>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {e && e.missingUsefulLife > 0 && (
        <Alert severity="warning">
          Für {e.missingUsefulLife === 1 ? 'eine Anlage' : `${e.missingUsefulLife} Anlagen`} fehlt
          noch die Nutzungsdauer – ihre Abschreibung ist in der EÜR noch nicht enthalten.
          {isAdmin ? ' Bitte unten im Anlagenverzeichnis erfassen.' : ' Der Admin ist informiert.'}
        </Alert>
      )}
      {loading && !e && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      )}

      {e && <EuerTable e={e} loading={loading} />}

      {e && (
        <Paper className="euer-break" sx={{ p: 2 }}>
          <Typography variant="h6">Von Einnahmen und Ausgaben zur EÜR</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Das Dashboard dient der Budgetsteuerung: Es zeigt, was tatsächlich bezahlt wurde, netto
            ohne Umsatzsteuer – auch die Anschaffung einer Anlage zählt dort mit dem vollen Betrag
            zum Zahlungsdatum. Die EÜR weicht davon an drei Stellen ab: (1) Anlagen über der
            GWG-Grenze – Sachanlagen wie immaterielle Vermögensgegenstände – zählen nicht mit ihren
            Anschaffungskosten, sondern nur mit ihrer Abschreibung im gewählten Zeitraum. MyBudget
            erkennt sie an der Kennzeichnung „Anlage“ der Belegposition in SevDesk; eine Anschaffung
            ohne diese Kennzeichnung zählt auch in der EÜR als Ausgabe. (2) Die Umsatzsteuer zählt
            mit: vereinnahmte Umsatzsteuer als Einnahme, gezahlte Vorsteuer als Ausgabe. (3) Ebenso
            Zahlungen an das und Erstattungen vom Finanzamt. GWG ändern das Ergebnis nicht – sie
            stehen in der EÜR nur unter den Abschreibungen statt in ihrer Gruppe.
          </Typography>
          <Table size="small">
            <TableBody>
              <Line
                label="Saldo Einnahmen − Ausgaben (wie Dashboard)"
                cents={e.reconciliation.saldo}
              />
              <Line
                label="+ Anschaffung von Anlagen über der GWG-Grenze (nicht in der EÜR)"
                cents={e.reconciliation.assetPurchases}
              />
              <Line label="− Abschreibung dieser Anlagen" cents={-e.reconciliation.depreciation} />
              <Line
                label="+ vereinnahmte Umsatzsteuer − gezahlte Vorsteuer"
                cents={e.reconciliation.vat}
              />
              <Line
                label="+ Erstattungen vom Finanzamt − Zahlungen an das Finanzamt"
                cents={e.reconciliation.finanzamt}
              />
              <Line
                label={`= ${e.result < 0 ? 'Verlust' : 'Gewinn'} laut EÜR`}
                cents={e.result}
                bold
              />
            </TableBody>
          </Table>
          <Box className={DETAIL} sx={{ mt: 2 }}>
            <Vouchers
              title="Anschaffung von Anlagen über der GWG-Grenze"
              rows={e.vouchers.assetPurchases}
              divisionName={divisionName}
            />
            <Vouchers
              title="GWG (in der EÜR unter Abschreibungen)"
              rows={e.vouchers.gwg}
              divisionName={divisionName}
            />
            <Vouchers
              title="Zahlungen an das und Erstattungen vom Finanzamt"
              rows={e.vouchers.finanzamt}
              divisionName={divisionName}
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            Die Umsatzsteuer der übrigen Belege steht je Beleg im CSV-Export der Buchungen (Spalten
            Brutto, USt, Netto) und für den Gesamtverein auf der Seite „Umsatzsteuer“.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            MyBudget schreibt Anlagen monatlich ab (linear ab dem Monat des Belegdatums). SevDesk
            bucht die Abschreibung mancher Anlagen erst später, z.B. zum Jahresende; unterjährig
            kann die EÜR in SevDesk deshalb weniger Abschreibung zeigen. Zum Jahresende stimmen
            beide überein, bis auf Cent-Rundungen der Monatsraten.
          </Typography>
        </Paper>
      )}

      {e && (
        <TableContainer component={Paper} className={DETAIL}>
          <Typography variant="h6" sx={{ p: 2, pb: 0 }}>
            Anlagenverzeichnis
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
            Anlagen über der GWG-Grenze (in SevDesk als Anlage gekennzeichnet), lineare Abschreibung
            ab dem Monat des Belegdatums.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Belegdatum</TableCell>
                <TableCell>Beleg / Bezeichnung</TableCell>
                <TableCell>Abteilung</TableCell>
                <TableCell {...num}>Anschaffung netto</TableCell>
                <TableCell {...num}>Nutzungsdauer</TableCell>
                <TableCell {...num}>Abschreibung im Zeitraum</TableCell>
                <TableCell {...num}>Restbuchwert</TableCell>
                {isAdmin && <TableCell sx={{ displayPrint: 'none' }} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {e.assets.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography color="text.secondary">Keine Anlagen im Zeitraum.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {e.assets.map((a) => (
                <TableRow key={a.postingId}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{germanDate(a.acquiredOn)}</TableCell>
                  <TableCell>
                    {a.voucherNumber}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {a.text ?? a.partner}
                    </Typography>
                  </TableCell>
                  <TableCell>{divisionName(a.divisionId)}</TableCell>
                  <TableCell {...num}>{euro(a.costCents)}</TableCell>
                  <TableCell {...num}>
                    {a.usefulLifeMonths ? (
                      `${a.usefulLifeMonths} Monate`
                    ) : (
                      <Typography variant="body2" color="warning.main">
                        fehlt
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell {...num}>{euro(a.depreciationCents)}</TableCell>
                  <TableCell {...num}>{euro(a.bookValueCents)}</TableCell>
                  {isAdmin && (
                    <TableCell sx={{ displayPrint: 'none' }}>
                      <Tooltip title="Nutzungsdauer erfassen">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setDialogError(undefined);
                            setMonths(a.usefulLifeMonths ? String(a.usefulLifeMonths) : '');
                            setEditing(a);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={Boolean(editing)} onClose={() => setEditing(undefined)} maxWidth="xs" fullWidth>
        <DialogTitle>Nutzungsdauer</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">
              {editing?.voucherNumber} {editing?.text ?? editing?.partner} –{' '}
              {editing && euro(editing.costCents)} netto
            </Typography>
            <TextField
              label="Nutzungsdauer in Monaten"
              value={months}
              onChange={(ev) => setMonths(ev.target.value)}
              helperText="z.B. 60 für 5 Jahre, wie in SevDesk"
              autoFocus
              fullWidth
            />
            {dialogError && <Alert severity="error">{dialogError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(undefined)}>Abbrechen</Button>
          <Button variant="contained" onClick={() => void saveUsefulLife()}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
